import '../app/components/Tooltip.css';
import '../app/style.css';
import React from 'react';
import { Route } from 'react-router-dom';
import { Spinner, Provider as TacoProvider } from '@economic/taco';
import { AuthRoutes, CriticalError, datadogInit, ErrorBoundary, getDetectedLocale, i18n, Locale, log } from '@economic/react-app';
import { I18nProvider } from '@lingui/react';

import { fetchLocalisation } from '~/fetchLocalisation';
import { DataProvider } from '~/app/shared/components/DataContext';
import { DATADOG_CONFIG } from '~/bootstrap';
import getEnvironment from '~/utilities/getEnvironment';
import { NotFound } from './pages/NotFound';
import pkg from '../../package.json';

const Document = React.lazy(() => import(/* webpackChunkName: "external.document" */ './pages/Document/Document'));
const SmartBank = React.lazy(() => import(/* webpackChunkName: "external.smartbank" */ './pages/SmartBank'));
const PaymentTakeover = React.lazy(() => import(/* webpackChunkName: "external.paymenttakeover" */ './pages/PaymentTakeover'));
const PaymentLinkProxy = React.lazy(
    () =>
        import(
            /* webpackChunkName: "external.paymentlink.paymentlinkproxy" */ '~/external/pages/CustomerPayments/PaymentLinkProxy'
        )
);
const PaymentSuccess = React.lazy(
    () => import(/* webpackChunkName: "external.paymentlink.paymentsuccess" */ '~/external/pages/CustomerPayments/PaymentSuccess')
);
const PaymentCancelled = React.lazy(
    () =>
        import(
            /* webpackChunkName: "external.paymentlink.paymentcancelled" */ '~/external/pages/CustomerPayments/PaymentCancelled'
        )
);
const PaymentError = React.lazy(
    () => import(/* webpackChunkName: "external.paymentlink.paymenterror" */ '~/external/pages/CustomerPayments/PaymentError')
);

export default function External() {
    const [error, setError] = React.useState<Error | null>(null);
    const [i18nLoaded, setI18nLoaded] = React.useState(false);
    const locale = getDetectedLocale() as Locale['languageCode'];

    // the user is unauthenticated, so we use defaults
    const tacoLocalisation = fetchLocalisation(locale, 'dd.mm.yy');
    const tacoSettings = { uniqueUserIdentifier: 'unauthenticated' };

    React.useEffect(() => {
        datadogInit(DATADOG_CONFIG.applicationId, DATADOG_CONFIG.clientToken, getEnvironment(), pkg.version);
    }, []);

    React.useEffect(() => {
        i18n.init(locale);
        i18n.load(locale, process.env.PUBLIC_URL + '/locales')
            .catch(err => {
                setError(err);
                log.error(err);
            })
            .finally(() => setI18nLoaded(true));
    }, [locale]);

    if (error) {
        return <CriticalError />;
    }

    if (!i18nLoaded) {
        return <Spinner className="w-full p-8" />;
    }

    return (
        <React.Suspense fallback={<Spinner className="w-full p-8" />}>
            <I18nProvider i18n={i18n.i18n}>
                <TacoProvider localization={tacoLocalisation} settings={tacoSettings}>
                    <DataProvider>
                        <ErrorBoundary>
                            <AuthRoutes>
                                <Route path="document/:key" element={<Document />} />
                                <Route path="smartbank/:key" element={<SmartBank />} />
                                <Route path="paymenttakeover/:key" element={<PaymentTakeover />} />
                                <Route path="paymentlink/payment/:token" element={<PaymentLinkProxy />} />
                                <Route path="paymentlink/payment/:token/success" element={<PaymentSuccess />} />
                                <Route path="paymentlink/payment/:token/cancelled" element={<PaymentCancelled />} />
                                <Route path="paymentlink/payment/error" element={<PaymentError />} />
                                <Route path="*" element={<NotFound />} />
                            </AuthRoutes>
                        </ErrorBoundary>
                    </DataProvider>
                </TacoProvider>
            </I18nProvider>
        </React.Suspense>
    );
}
